import { bucketProblem, DOCUMENT_PURPOSE, MAX_FILE_NAME, VAULT_KINDS, type PayloadOf, type VaultKind } from '@gk/contracts'
import { fmtNum } from '../lib/format.ts'
import { useMerged } from '../sync/hooks.ts'
import type { Pending } from '../sync/merge.ts'

/** M15-V, the document vault (docs/M15.md V): the club's labels, safety-data sheets, notices and policies, each a document with immutable versions
 *  (core.document / core.document_version, mirrored), a version's file named by ONE link (purpose 'document') that says where its bytes are — the device never
 *  reads the registry of files. Procedures are documents too, but stay in Planning › Réglages: the vault lists the four kinds of `VAULT_KINDS`. Pure: the
 *  rows, the form's checks (the server's first refusals, said first), the payloads, the words of a file; the screens read the mirror only (offline), and a
 *  file is handed over on demand (photos/lane.ts `readDocument`), never previewed. */
export type Bilingual = { fr?: string; en?: string }
export interface VaultDocumentRow { id: string; kind: 'procedure' | VaultKind; site_id?: string }
export interface VaultVersionRow {
  id: string; document_id: string; version_no: number; status: 'published' | 'retired'; title: Bilingual; summary: Bilingual | null; body: Bilingual | null
  file_id?: string | null; effective_from: string; published_at: string; retired_at?: string | null
}
/** A row of the device's `core.attachment_link` mirror (a pending one from a projector carries the same fields). */
export interface VaultLink {
  id: string; org_id?: string; site_id: string; file_id: string; sha256: string; bytes: number; mime: string; entity_type: string; entity_id: string; purpose: string
  caption: string | null; captured_at: string | null; hidden_at: string | null; bucket?: string
}

export const useVaultDocuments = () => useMerged<VaultDocumentRow>('document', 'core.document')
export const useVaultVersions = () => useMerged<VaultVersionRow>('document_version', 'core.document_version')
export const useVaultLinks = () => useMerged<VaultLink>('attachment_link', 'core.attachment_link')

export const isVaultKind = (k: string): k is VaultKind => (VAULT_KINDS as readonly string[]).includes(k)
export const titleOf = (t: Bilingual | null | undefined, lang: string): string => (lang.startsWith('en') ? t?.en ?? t?.fr : t?.fr ?? t?.en) ?? ''

/** The file of a version: its one link with the purpose 'document' (never a hidden one). */
export function fileLinkOf<L extends VaultLink>(links: readonly L[], versionId: string): L | undefined {
  return links.find((l) => l.entity_type === 'core.document_version' && l.entity_id === versionId && l.purpose === DOCUMENT_PURPOSE && !l.hidden_at)
}

export interface VaultEntry {
  doc: VaultDocumentRow
  /** the version in force (published), if any: a retired document has none */
  current: Pending<VaultVersionRow> | undefined
  /** the newest version, in force or not (what a retired document shows) */
  latest: Pending<VaultVersionRow>
  /** every version, newest first */
  history: Array<Pending<VaultVersionRow>>
  /** the file of the version shown (the one in force, else the newest) */
  file: VaultLink | undefined
}
/** The vault's lists, one per kind: the documents in force first, by title, then the retired ones, by title. A document with no version on this device yet
 *  (its first publish still on its way) is left out until its version arrives. */
export function vaultEntries(docs: readonly VaultDocumentRow[], versions: ReadonlyArray<Pending<VaultVersionRow>>, links: readonly VaultLink[], lang: string): Record<VaultKind, VaultEntry[]> {
  const out = Object.fromEntries(VAULT_KINDS.map((k) => [k, [] as VaultEntry[]])) as Record<VaultKind, VaultEntry[]>
  const byDoc = new Map<string, Array<Pending<VaultVersionRow>>>()
  for (const v of versions) byDoc.set(v.document_id, [...(byDoc.get(v.document_id) ?? []), v])
  for (const doc of docs) {
    if (!isVaultKind(doc.kind)) continue
    const history = (byDoc.get(doc.id) ?? []).slice().sort((a, b) => b.version_no - a.version_no)
    const latest = history[0]
    if (!latest) continue
    const current = history.find((v) => v.status === 'published')
    out[doc.kind].push({ doc, current, latest, history, file: fileLinkOf(links, (current ?? latest).id) })
  }
  for (const k of VAULT_KINDS) {
    out[k].sort((a, b) => Number(!a.current) - Number(!b.current) || titleOf((a.current ?? a.latest).title, lang).localeCompare(titleOf((b.current ?? b.latest).title, lang), lang) || a.doc.id.localeCompare(b.doc.id))
  }
  return out
}

/** "48 ko", "2,4 Mo" (a French club; "48 KB", "2.4 MB" in English): a size for people, never a precise count. */
export function sizeText(bytes: number, lang: string): string {
  const en = lang.startsWith('en')
  if (bytes < 1024 * 1024) return `${fmtNum(Math.max(1, Math.round(bytes / 1024)), lang, 0)} ${en ? 'KB' : 'ko'}`
  return `${fmtNum(bytes / (1024 * 1024), lang, 1)} ${en ? 'MB' : 'Mo'}`
}
/** The type a person recognises. */
export function typeText(mime: string): string {
  return ({ 'application/pdf': 'PDF', 'image/jpeg': 'JPEG', 'image/png': 'PNG', 'image/webp': 'WebP' } as Record<string, string>)[mime] ?? mime
}
/** The name a download is saved under: the file's own (the link's caption) when there is one, else the title with the version, made safe for a file system. */
export function downloadName(file: Pick<VaultLink, 'caption' | 'mime'>, title: string, versionNo: number): string {
  const ext = file.mime === 'application/pdf' ? 'pdf' : file.mime === 'image/png' ? 'png' : file.mime === 'image/webp' ? 'webp' : 'jpg'
  const own = (file.caption ?? '').trim()
  if (own) return /\.[a-z0-9]{1,5}$/i.test(own) ? own : `${own}.${ext}`
  const base = title.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80) || 'document'
  return `${base}-v${versionNo}.${ext}`
}

// ---------------------------------------------------------------------------------------------------------- the form
/** the file chosen on the form: what the publish names (the bytes stay with the page until the publish is sent) */
export interface DraftFile { sha256: string; bytes: number; mime: string; capturedAt: string; name: string }
export interface VaultDraft { kind: VaultKind; titleFr: string; titleEn: string; note: string; file: DraftFile | null }
export const blankVaultDraft = (kind: VaultKind = 'label'): VaultDraft => ({ kind, titleFr: '', titleEn: '', note: '', file: null })
/** The next version's form starts from the version shown: its titles and note; the file is chosen again (a version is its file). */
export const draftOfEntry = (e: VaultEntry): VaultDraft => {
  const v = e.current ?? e.latest
  return { kind: isVaultKind(e.doc.kind) ? e.doc.kind : 'label', titleFr: v.title.fr ?? '', titleEn: v.title.en ?? '', note: v.summary?.fr ?? v.summary?.en ?? '', file: null }
}

export const VAULT_ERRORS = ['TITLE_MISSING', 'TITLE_TOO_LONG', 'NOTE_TOO_LONG', 'FILE_MISSING', 'ATTACHMENT_TYPE_NOT_ALLOWED', 'ATTACHMENT_TOO_LARGE'] as const
export type VaultError = (typeof VAULT_ERRORS)[number]
const cp = (s: string) => [...s.trim()].length
/** Why the version cannot be published yet (shown as `documents.err.<CODE>`): a title in one language at least (120 at most), a note of 4000 at most, a
 *  file, of a type and a size the documents bucket takes (what the server would refuse first). The server judges again. */
export function vaultProblems(d: VaultDraft): VaultError[] {
  const out: VaultError[] = []
  if (!d.titleFr.trim() && !d.titleEn.trim()) out.push('TITLE_MISSING')
  if (cp(d.titleFr) > 120 || cp(d.titleEn) > 120) out.push('TITLE_TOO_LONG')
  if (cp(d.note) > 4000) out.push('NOTE_TOO_LONG')
  if (!d.file) out.push('FILE_MISSING')
  else {
    const p = bucketProblem('documents', d.file.mime, d.file.bytes)
    if (p === 'ATTACHMENT_TYPE_NOT_ALLOWED' || p === 'ATTACHMENT_TOO_LARGE') out.push(p)
  }
  return out
}
/** `document.publish_version@2`: the titles and the note trimmed (an empty language left out), the kind only for a new document (a document's kind never
 *  changes), the file with its name cut to the schema's bound. */
export function buildVaultPublish(ids: { documentId: string; versionId: string }, d: VaultDraft, isNew: boolean): PayloadOf<'document.publish_version@2'> {
  const title: Bilingual = {}
  if (d.titleFr.trim()) title.fr = d.titleFr.trim()
  if (d.titleEn.trim()) title.en = d.titleEn.trim()
  const note = d.note.trim()
  const f = d.file
  return {
    document_id: ids.documentId, version_id: ids.versionId, ...(isNew ? { kind: d.kind } : {}), title: title as PayloadOf<'document.publish_version@2'>['title'],
    ...(note ? { summary: { fr: note } } : {}),
    ...(f ? { file: { sha256: f.sha256, bytes: f.bytes, mime: f.mime as 'application/pdf', captured_at: f.capturedAt, name: [...f.name.trim()].slice(0, MAX_FILE_NAME).join('') || null } } : {}),
  }
}

// ---------------------------------------------------------------------------------------------------------- the pickers (a product's label, a verification)
export interface VersionOption { id: string; title: string; versionNo: number; file: VaultLink | undefined }
/** The versions IN FORCE of one kind (a label, a safety-data sheet), by title: what a product version may point at and a verification may name. */
export function versionOptions(docs: readonly VaultDocumentRow[], versions: ReadonlyArray<Pending<VaultVersionRow>>, links: readonly VaultLink[], kind: 'label' | 'sds', lang: string): VersionOption[] {
  const ofKind = new Set(docs.filter((d) => d.kind === kind).map((d) => d.id))
  return versions.filter((v) => v.status === 'published' && ofKind.has(v.document_id))
    .map((v) => ({ id: v.id, title: titleOf(v.title, lang), versionNo: v.version_no, file: fileLinkOf(links, v.id) }))
    .sort((a, b) => a.title.localeCompare(b.title, lang) || a.id.localeCompare(b.id))
}
/** What a picker starts on: the version the record names when it is still offered (in force), else none. A replaced label is never pre-selected. */
export const preselected = (named: string | null | undefined, options: readonly VersionOption[]): string => (named && options.some((o) => o.id === named) ? named : '')
/** One line for an option: the title, the version, and the file's type and size when it has one. */
export function optionText(o: VersionOption, lang: string, versionWord: (n: number) => string): string {
  return [o.title, versionWord(o.versionNo), o.file ? `${typeText(o.file.mime)} ${sizeText(o.file.bytes, lang)}` : null].filter(Boolean).join(' · ')
}
